import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Pencil, Check, X, CalendarRange } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { kursbestandAPI } from '@/lib/api';

/* Stammkurse an der Lead-Karte (18.09.2026): dieselbe Zuordnung wie im
   Kursbestand, nur direkt beim Mitglied. Gruppe aus dem Tarif wird fett
   vorgeschlagen, der Wochentag wird angekreuzt. Recht: leads.edit. */

const TAGE = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];

export default function StammkurseBlock({ leadId, lead, darfBearbeiten, onChange }) {
  const [kurse, setKurse] = useState(null);
  const [bearbeitet, setBearbeitet] = useState(false);
  const [gewaehlt, setGewaehlt] = useState(new Set(lead?.stammkurse || []));
  const [speichert, setSpeichert] = useState(false);

  useEffect(() => { setGewaehlt(new Set(lead?.stammkurse || [])); }, [lead?.stammkurse]);
  useEffect(() => { kursbestandAPI.kurse().then(r => setKurse(r.data?.courses || [])).catch(() => setKurse([])); }, []);

  const zugeordnet = (lead?.stammkurse || []).map(id => (kurse || []).find(k => k.id === id)).filter(Boolean);
  const gruppe = lead?.stammkurs_gruppe;
  const toggle = (id) => setGewaehlt(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const speichern = async () => {
    setSpeichert(true);
    try { await kursbestandAPI.zuordnen(leadId, [...gewaehlt]); toast.success(`${gewaehlt.size} Kurs${gewaehlt.size === 1 ? '' : 'e'} gespeichert`); setBearbeitet(false); onChange?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    finally { setSpeichert(false); }
  };

  return (
    <Card className="border-border/60" data-testid="stammkurse-block">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5"><CalendarRange className="w-4 h-4 text-marke" /> Stammkurse</span>
          {darfBearbeiten && !bearbeitet && <button className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-accent" title="Kurse zuordnen" onClick={() => setBearbeitet(true)} data-testid="stammkurse-edit"><Pencil className="w-3.5 h-3.5" /></button>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-xs">
        {!bearbeitet && (
          zugeordnet.length
            ? <ul className="space-y-0.5">{zugeordnet.map(k => <li key={k.id}><span className="tabular-nums text-muted-foreground">{k.day.slice(0, 2)} {k.start}</span> · <span className="font-medium">{k.name}</span>{k.note ? <span className="text-muted-foreground"> · {k.note}</span> : ''}</li>)}</ul>
            : <p className="text-muted-foreground">{gruppe ? <span><b>{gruppe}</b> laut Tarif — Wochentag noch nicht zugeordnet.</span> : 'Noch kein Kurs zugeordnet.'}</p>
        )}
        {bearbeitet && (
          kurse === null ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /> : (
            <div className="space-y-2">
              {gruppe && !gewaehlt.size && <p className="text-muted-foreground">Aus dem Tarif: <b>{gruppe}</b> — bitte den Wochentag ankreuzen.</p>}
              {TAGE.map(tag => {
                const desTages = kurse.filter(k => k.day === tag);
                if (!desTages.length) return null;
                return (
                  <div key={tag}>
                    <p className="font-semibold text-muted-foreground uppercase tracking-wider text-[10px]">{tag}</p>
                    {desTages.map(k => (
                      <label key={k.id} className={`flex items-center gap-2 py-0.5 cursor-pointer ${gruppe && k.name === gruppe ? 'font-semibold' : ''}`}>
                        <input type="checkbox" className="accent-marke" checked={gewaehlt.has(k.id)} onChange={() => toggle(k.id)} />
                        <span className="tabular-nums text-muted-foreground w-10">{k.start}</span>
                        <span className="flex-1">{k.name}</span>
                      </label>
                    ))}
                  </div>
                );
              })}
              <div className="flex gap-2 pt-1">
                <Button size="sm" className="h-7 text-xs gap-1" onClick={speichern} disabled={speichert}>{speichert ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Speichern</Button>
                <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={() => { setGewaehlt(new Set(lead?.stammkurse || [])); setBearbeitet(false); }}><X className="w-3 h-3" /> Abbrechen</Button>
              </div>
            </div>
          )
        )}
      </CardContent>
    </Card>
  );
}
